// src/pages/business/reviews/CustomerReviewsPage.tsx
// Customer Reviews page for business portal - mimics mobile app review functionality

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Star,
  StarHalf,
  User,
  Calendar,
  MessageSquare,
  Filter,
  Loader2,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface Review {
  id: string;
  customer_name: string;
  customer_avatar?: string;
  rating: number;
  comment: string;
  product_name?: string;
  product_id?: string;
  service_name?: string;
  service_id?: string;
  created_at: string;
  helpful_count?: number;
  is_verified_purchase?: boolean;
}

interface RatingSummary {
  average: number;
  total: number;
  distribution: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ── Star Rating Component ─────────────────────────────────────────────────────
const StarRating: React.FC<{ rating: number; size?: number }> = ({ rating, size = 16 }) => {
  const stars = [];
  const normalizedRating = Math.min(Math.max(rating, 0), 5);

  for (let i = 1; i <= 5; i++) {
    if (i <= normalizedRating) {
      stars.push(
        <Star key={i} size={size} className="fill-yellow-400 text-yellow-400" />
      );
    } else if (i - normalizedRating < 1 && i - normalizedRating > 0) {
      stars.push(
        <StarHalf key={i} size={size} className="fill-yellow-400 text-yellow-400" />
      );
    } else {
      stars.push(<Star key={i} size={size} className="text-gray-300 dark:text-gray-600" />);
    }
  }

  return <div className="flex items-center gap-0.5">{stars}</div>;
};

// ── Rating Bar Component ──────────────────────────────────────────────────────
const RatingBar: React.FC<{
  stars: number;
  count: number;
  total: number;
}> = ({ stars, count, total }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-light-text dark:text-dark-text w-8">{stars}★</span>
      <div className="flex-1 h-2 bg-light-border dark:bg-dark-border rounded-full overflow-hidden">
        <div
          className="h-full bg-yellow-400 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs text-light-subtle dark:text-dark-subtle w-8 text-right">
        {count}
      </span>
    </div>
  );
};

// ── Review Card Component ─────────────────────────────────────────────────────
const ReviewCard: React.FC<{ review: Review }> = ({ review }) => {
  return (
    <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 mb-3 border border-light-border dark:border-dark-border">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            {review.customer_avatar ? (
              <img src={review.customer_avatar} alt={review.customer_name} className="w-full h-full rounded-full object-cover" />
            ) : (
              <User size={20} className="text-primary" />
            )}
          </div>
          <div>
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 14 }}>
              {review.customer_name}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <StarRating rating={review.rating} size={12} />
              <span className="text-xs text-light-subtle dark:text-dark-subtle">
                {formatDate(review.created_at)}
              </span>
            </div>
          </div>
        </div>

        {review.is_verified_purchase && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300">
            Verified Purchase
          </span>
        )}
      </div>

      {/* Product/Service Info */}
      {(review.product_name || review.service_name) && (
        <div className="mb-3">
          <span className="text-xs text-light-subtle dark:text-dark-subtle">
            Reviewing:{' '}
            <span className="text-primary font-medium">
              {review.product_name || review.service_name}
            </span>
          </span>
        </div>
      )}

      {/* Comment */}
      <p className="text-light-text dark:text-dark-text" style={{ fontSize: 14, lineHeight: 1.5 }}>
        {review.comment}
      </p>

      {/* Helpful Count */}
      {review.helpful_count !== undefined && review.helpful_count > 0 && (
        <div className="flex items-center gap-1 mt-3">
          <ThumbsUp size={12} className="text-light-subtle dark:text-dark-subtle" />
          <span className="text-xs text-light-subtle dark:text-dark-subtle">
            {review.helpful_count} found this helpful
          </span>
        </div>
      )}
    </div>
  );
};

// ── Main Page Component ────────────────────────────────────────────────────────
const CustomerReviewsPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { error: toastError } = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [ratingSummary, setRatingSummary] = useState<RatingSummary | null>(null);
  const [filterRating, setFilterRating] = useState<number>(0);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/businesses/${businessId}/reviews`);
      
      setReviews(res.data.reviews || []);
      setRatingSummary(res.data.summary || null);
    } catch (error: any) {
      console.error('Failed to fetch reviews:', error);
      toastError('Failed to load reviews');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [businessId]);

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchReviews();
  };

  const filteredReviews = useMemo(() => {
    if (filterRating === 0) return reviews;
    return reviews.filter((r) => r.rating === filterRating);
  }, [reviews, filterRating]);

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Customer Reviews
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <Loader2 size={40} className="animate-spin mx-auto text-primary" />
            <p className="mt-4 text-light-subtle dark:text-dark-subtle">Loading reviews...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Customer Reviews
        </h1>
        <button onClick={onRefresh} disabled={refreshing} className="p-2">
          <RefreshCw size={20} className={`text-primary ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Rating Summary */}
        {ratingSummary && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 mb-5 border border-light-border dark:border-dark-border">
            <div className="flex items-center gap-6 mb-4">
              {/* Average Rating */}
              <div className="text-center">
                <p className="text-4xl font-bold text-light-text dark:text-dark-text">
                  {ratingSummary.average.toFixed(1)}
                </p>
                <StarRating rating={ratingSummary.average} size={18} />
                <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">
                  {ratingSummary.total} reviews
                </p>
              </div>

              {/* Rating Distribution */}
              <div className="flex-1 space-y-1.5">
                {[5, 4, 3, 2, 1].map((stars) => (
                  <RatingBar
                    key={stars}
                    stars={stars}
                    count={ratingSummary.distribution[stars as keyof typeof ratingSummary.distribution] || 0}
                    total={ratingSummary.total}
                  />
                ))}
              </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-light-border dark:border-dark-border">
              <button
                onClick={() => setFilterRating(0)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filterRating === 0
                    ? 'bg-primary text-white'
                    : 'bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text hover:bg-light-border dark:hover:bg-dark-border'
                }`}
              >
                All Reviews
              </button>
              {[5, 4, 3, 2, 1].map((stars) => {
                const count = ratingSummary.distribution[stars as keyof typeof ratingSummary.distribution] || 0;
                if (count === 0) return null;
                return (
                  <button
                    key={stars}
                    onClick={() => setFilterRating(stars)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filterRating === stars
                        ? 'bg-primary text-white'
                        : 'bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text hover:bg-light-border dark:hover:bg-dark-border'
                    }`}
                  >
                    {stars}★ ({count})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Reviews List */}
        {filteredReviews.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <MessageSquare size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              {filterRating > 0 ? `No ${filterRating}-star reviews` : 'No reviews yet'}
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              {filterRating > 0
                ? 'Try selecting a different filter'
                : 'Reviews from customers will appear here'}
            </p>
          </div>
        ) : (
          <div>
            <p className="text-sm text-light-subtle dark:text-dark-subtle mb-4">
              Showing {filteredReviews.length} review{filteredReviews.length !== 1 ? 's' : ''}
              {filterRating > 0 && ` (${filterRating} stars)`}
            </p>
            {filteredReviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerReviewsPage;